import { api } from './api';
import { League } from './leagueService';
import { Team } from './teamService';
import { authManager } from './authManager';

export interface ImportLeagueRequest {
  url: string;
  importSchedules?: boolean;
  makePublic?: boolean;
}

export interface ImportLeagueResponse {
  league: League;
  teams: Team[];
  message: string;
  scheduleImportStatus: 'pending' | 'in_progress' | 'completed' | 'failed';
  action?: 'created' | 'replaced' | 'merged';
  importId?: string; // For async imports
}

export interface DuplicateLeagueError {
  error: 'DUPLICATE_LEAGUE';
  message: string;
  existingLeague: {
    id: string;
    name: string;
    description: string;
    website: string;
    createdAt: string;
    updatedAt: string;
    teamCount: number;
    matchCount: number;
    teams: Array<{ id: string; name: string }>;
  };
  options: {
    replace: {
      action: 'replace';
      description: string;
      warning: string;
    };
    merge: {
      action: 'merge';
      description: string;
      warning: string;
    };
    cancel: {
      action: 'cancel';
      description: string;
    };
  };
}

export interface ResolveDuplicateRequest {
  url: string;
  action: 'replace' | 'merge' | 'cancel';
  makePublic?: boolean;
}

export interface ResolveDuplicateResponse {
  success: boolean;
  message: string;
  action: 'cancelled' | 'replaced' | 'merged';
  league?: League;
  teams?: Team[];
}

export interface ValidateUrlResponse {
  valid: boolean;
  platform: 'GOTSPORT' | 'SINCSPORTS' | null;
  urlType?: 'event' | 'schedule';
  requiresDiscovery?: boolean;
  leagueInfo?: {
    name: string;
    description?: string;
    region?: string;
    estimatedTeams: number;
    hasSchedules: boolean;
  };
  error?: string;
}

// GotSport discovery types
export interface ScheduleGroup {
  id: string;
  name: string;
  ageGroup?: string;
  gender?: string;
  division?: string;
  scheduleUrl: string;
}

export interface DiscoveryResponse {
  success: boolean;
  eventId: string;
  eventName: string;
  groups: ScheduleGroup[];
  cachedAt?: string;
  error?: string;
}

// SincSports discovery types
export interface SincSportsDivision {
  id: string;
  name: string;
  ageGroup?: string;
  gender?: string;
  division?: string;
  scheduleUrl: string;
}

export interface SincSportsDiscoveryResponse {
  success: boolean;
  tournamentId: string;
  leagueName: string;
  divisions: SincSportsDivision[];
  cachedAt?: string;
  error?: string;
}

export interface SincSportsDiscoveryProgress {
  type: 'connected' | 'progress' | 'result' | 'error' | 'done';
  phase?: 'connecting' | 'fetching_page' | 'extracting_divisions' | 'processing' | 'complete' | 'error';
  percent?: number;
  message?: string;
  detail?: string;
  divisionsFound?: number;
  divisionsProcessed?: number;
  // Result fields (when type === 'result')
  success?: boolean;
  tournamentId?: string;
  leagueName?: string;
  divisions?: SincSportsDivision[];
  error?: string;
}

export interface ImportProgressResponse {
  status: 'pending' | 'importing_teams' | 'importing_schedules' | 'completed' | 'failed';
  progress: number;
  total: number;
  current: number;
  currentTeam?: string;
  message?: string;
  error?: string;
}

// Discovery progress types for SSE streaming
export interface DiscoveryProgress {
  type: 'connected' | 'progress' | 'result' | 'error' | 'done';
  phase?: 'connecting' | 'fetching_page' | 'extracting_groups' | 'processing' | 'complete' | 'error';
  percent?: number;
  message?: string;
  detail?: string;
  groupsFound?: number;
  groupsProcessed?: number;
  // Result fields (when type === 'result')
  success?: boolean;
  eventId?: string;
  eventName?: string;
  groups?: ScheduleGroup[];
  error?: string;
}

export const importService = {
  // Validate import URL
  async validateUrl(url: string): Promise<ValidateUrlResponse> {
    const response = await api.post('/leagues/import/validate', { url });
    return response.data;
  },

  // Import league from URL
  async importLeague(data: ImportLeagueRequest): Promise<ImportLeagueResponse> {
    try {
      const response = await api.post('/leagues/import', {
        url: data.url,
        importSchedules: data.importSchedules ?? true,
        makePublic: data.makePublic ?? true
      });
      return response.data;
    } catch (error: any) {
      // Check if it's a duplicate league error
      if (error.response?.status === 409 && error.response?.data?.error === 'DUPLICATE_LEAGUE') {
        // Re-throw with the duplicate data for the UI to handle
        throw {
          isDuplicateError: true,
          duplicateData: error.response.data as DuplicateLeagueError
        };
      }
      throw error;
    }
  },

  // Resolve duplicate league import
  async resolveDuplicateLeague(data: ResolveDuplicateRequest): Promise<ResolveDuplicateResponse> {
    const response = await api.post('/leagues/import/resolve-duplicate', {
      url: data.url,
      action: data.action,
      makePublic: data.makePublic ?? true
    });
    return response.data;
  },

  // Get import progress
  async getImportProgress(importId: string): Promise<ImportProgressResponse> {
    const response = await api.get(`/leagues/import/${importId}/progress`);
    return response.data;
  },

  // Test import configuration
  async testImport(platform: 'GOTSPORT' | 'SINCSPORTS', url: string): Promise<any> {
    const response = await api.post('/importing/test', {
      platform,
      url
    });
    return response.data;
  },

  // Discover schedule groups from GotSport event URL
  async discoverEventGroups(url: string): Promise<DiscoveryResponse> {
    const response = await api.post('/leagues/import/discover', { url });
    return response.data;
  },

  // Discover schedule groups with SSE progress streaming
  // Returns callbacks to handle progress updates
  async discoverEventGroupsWithProgress(
    url: string,
    callbacks: {
      onProgress: (progress: DiscoveryProgress) => void;
      onResult: (result: DiscoveryResponse) => void;
      onError: (error: string) => void;
    }
  ): Promise<{ close: () => void }> {
    // Get the base URL from the api instance (e.g., /api/v1)
    const baseUrl = api.defaults.baseURL || '/api/v1';
    // Ensure we have a fresh token before connecting
    await authManager.getAccessToken();
    const token = authManager.getCurrentToken();

    // Build SSE URL with auth token as query param (SSE doesn't support custom headers)
    const sseUrl = `${baseUrl}/leagues/import/discover/stream?url=${encodeURIComponent(url)}&token=${encodeURIComponent(token || '')}`;

    const eventSource = new EventSource(sseUrl);
    let hasReceivedResult = false;

    eventSource.onmessage = (event) => {
      try {
        const data: DiscoveryProgress = JSON.parse(event.data);

        if (data.type === 'progress') {
          callbacks.onProgress(data);
        } else if (data.type === 'result') {
          hasReceivedResult = true;
          callbacks.onResult({
            success: data.success || false,
            eventId: data.eventId || '',
            eventName: data.eventName || '',
            groups: data.groups || [],
            error: data.error
          });
        } else if (data.type === 'error') {
          callbacks.onError(data.error || 'Unknown error');
          eventSource.close();
        } else if (data.type === 'done') {
          eventSource.close();
        }
      } catch (e) {
        console.error('Failed to parse SSE message:', e);
      }
    };

    eventSource.onerror = () => {
      // Only report connection lost if we haven't received a successful result
      // When the server closes the connection after 'done', onerror fires but it's expected
      if (!hasReceivedResult) {
        callbacks.onError('Connection lost');
      }
      eventSource.close();
    };

    return {
      close: () => eventSource.close()
    };
  },

  // Fetch teams from a group's schedule URL for preview
  async fetchGroupTeams(scheduleUrl: string): Promise<{ success: boolean; teams: string[]; count: number; error?: string }> {
    const response = await api.get('/leagues/import/discover/teams', {
      params: { scheduleUrl }
    });
    return response.data;
  },

  // Discover divisions from SincSports league URL
  async discoverSincSportsDivisions(url: string): Promise<SincSportsDiscoveryResponse> {
    const response = await api.post('/leagues/import/sincsports/discover', { url });
    return response.data;
  },

  // Discover SincSports divisions with SSE progress streaming
  // Returns callbacks to handle progress updates
  async discoverSincSportsDivisionsWithProgress(
    url: string,
    callbacks: {
      onProgress: (progress: SincSportsDiscoveryProgress) => void;
      onResult: (result: SincSportsDiscoveryResponse) => void;
      onError: (error: string) => void;
    }
  ): Promise<{ close: () => void }> {
    // Get the base URL from the api instance (e.g., /api/v1)
    const baseUrl = api.defaults.baseURL || '/api/v1';
    // Ensure we have a fresh token before connecting
    await authManager.getAccessToken();
    const token = authManager.getCurrentToken();

    // Build SSE URL with auth token as query param (SSE doesn't support custom headers)
    const sseUrl = `${baseUrl}/leagues/import/sincsports/discover/stream?url=${encodeURIComponent(url)}&token=${encodeURIComponent(token || '')}`;

    const eventSource = new EventSource(sseUrl);
    let hasReceivedResult = false;

    eventSource.onmessage = (event) => {
      try {
        const data: SincSportsDiscoveryProgress = JSON.parse(event.data);

        if (data.type === 'progress') {
          callbacks.onProgress(data);
        } else if (data.type === 'result') {
          hasReceivedResult = true;
          callbacks.onResult({
            success: data.success || false,
            tournamentId: data.tournamentId || '',
            leagueName: data.leagueName || '',
            divisions: data.divisions || [],
            error: data.error
          });
        } else if (data.type === 'error') {
          callbacks.onError(data.error || 'Unknown error');
          eventSource.close();
        } else if (data.type === 'done') {
          eventSource.close();
        }
      } catch (e) {
        console.error('Failed to parse SSE message:', e);
      }
    };

    eventSource.onerror = () => {
      // Only report connection lost if we haven't received a successful result
      // When the server closes the connection after 'done', onerror fires but it's expected
      if (!hasReceivedResult) {
        callbacks.onError('Connection lost');
      }
      eventSource.close();
    };

    return {
      close: () => eventSource.close()
    };
  },

  // Fetch teams from a SincSports division's schedule URL for preview
  async fetchSincSportsDivisionTeams(scheduleUrl: string): Promise<{ success: boolean; teams: string[]; count: number; error?: string }> {
    const response = await api.get('/leagues/import/sincsports/discover/teams', {
      params: { scheduleUrl }
    });
    return response.data;
  }
};
